import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import TableRowSummary from '../../../components/table-row-summary/table-row-summary.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import {
  DEAL_BUILDING_DETAILS_ITEM_CREATE, DEAL_BUILDING_DETAILS_ITEM_DELETE, DEAL_BUILDING_DETAILS_ITEM_EDIT,
} from '../../../constants/entity/permissions';

const DealAssetUseBreakdown: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ASSET_USE_BREAKDOWN_FIELD_LIMIT: number = 5;

  const {
    isEditMode,
    isNewDeal,
    dealBuildingDetailsData,
    dealAssetUseBreakdownValues,
    dealAssetUseBreakdownNewItems,
    dealAssetUseBreakdownNewFieldItems,
    dealAssetUseBreakdownDefaultProperties,
    assetTypeList,
    measureUnitList,
    onAssetUseBreakdownAddNewItem,
    onAssetUseBreakdownDeleteClick,
    onAssetUseBreakdownAddNewField,
    onAssetUseBreakdownDeleteField,
    onAssetTypeChange,
  } = props;

  const [dealBuildingDetailsItems, setDealBuildingDetailsItems]: [DealBuildingDetailsData | null, Function]
    = useState<StateValueDefault>(null);
  const [dealAssetUseBreakdownTotalItems, setDealAssetUseBreakdownTotalItems]: [Record<string, number> | null, Function]
    = useState<StateValueDefault>(null);

  const measureUnitMap: Record<string, MeasureUnit> = useMemo((): Record<string, MeasureUnit> => (
    StructureService.getItemMap(measureUnitList, 'name')
  ), [measureUnitList]);

  const dealBuildingDetailsItemEditPermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_ITEM_EDIT,
  ], []);
  const dealBuildingDetailsItemCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_ITEM_CREATE,
  ], []);
  const dealBuildingDetailsItemDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_ITEM_DELETE,
  ], []);

  const isAddNewFieldLimit: boolean = !!dealBuildingDetailsItems
    && dealBuildingDetailsItems.assetUseBreakdownHeaders.length
    + dealAssetUseBreakdownNewFieldItems.length >= ASSET_USE_BREAKDOWN_FIELD_LIMIT;
  const withHeaders: boolean = (dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdownHeaders
      && !!dealBuildingDetailsItems.assetUseBreakdownHeaders.length)
    || (dealAssetUseBreakdownNewFieldItems && !!dealAssetUseBreakdownNewFieldItems.length);
  const columnsTotal: number = (dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdownHeaders
      ? dealBuildingDetailsItems.assetUseBreakdownHeaders.length : 0)
    + dealAssetUseBreakdownNewFieldItems.length
    + (dealAssetUseBreakdownNewItems.length ? 1 : 0) + (!isAddNewFieldLimit ? 1 : 0) + 1;
  const formPrefixBreakdown: string = 'dealData.dealBuildingDetailsData.assetUseBreakdown';
  const formPrefixDealAssetUseBreakdownHeaders: string = 'dealData.dealBuildingDetailsData.assetUseBreakdownHeaders';

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionList: assetTypeList,
  }), [assetTypeList]);

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAssetUseBreakdownAddNewItem) {
      onAssetUseBreakdownAddNewItem({
        id: StructureService.getNewItemId(
          dealAssetUseBreakdownNewItems,
          (dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdown) || undefined,
        ),
        name: '',
        assetType: null,
        breakdownProperties: dealAssetUseBreakdownDefaultProperties,
      });
    }
  }, [dealBuildingDetailsItems, dealAssetUseBreakdownNewItems, dealAssetUseBreakdownDefaultProperties]);

  const addNewFieldDealAssetUseBreakdown: MouseEventHandlerFunction = useCallback((): void => {
    const newFieldId: number = StructureService.getNewItemId(
      dealAssetUseBreakdownNewFieldItems,
      (dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdownHeaders) || undefined,
    );

    if (onAssetUseBreakdownAddNewField) {
      onAssetUseBreakdownAddNewField({
        id: newFieldId,
        name: '',
      });
    }
  }, [dealBuildingDetailsItems, dealAssetUseBreakdownNewItems, dealAssetUseBreakdownNewFieldItems]);

  const deleteField: Function = useCallback((fieldId: number): void => {
    if (onAssetUseBreakdownDeleteField) {
      onAssetUseBreakdownDeleteField(fieldId);
    }
  }, [dealAssetUseBreakdownNewItems, dealAssetUseBreakdownNewFieldItems]);

  const dealAssetUseBreakdownDeleteClick: Function = useCallback((dealAssetUseBreakdownId: number): void => {
    if (onAssetUseBreakdownDeleteClick) {
      onAssetUseBreakdownDeleteClick(dealAssetUseBreakdownId);
    }

    if (dealAssetUseBreakdownValues) {
      setDealBuildingDetailsItems({
        ...(dealBuildingDetailsItems || {}),
        assetUseBreakdown: dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdown
          .filter((dealAssetUseBreakdownItem: DealAssetUseBreakdownItem): boolean => (
            dealAssetUseBreakdownItem.id !== dealAssetUseBreakdownId
          )),
      });
    }
  }, [
    dealBuildingDetailsItems, dealAssetUseBreakdownValues, dealAssetUseBreakdownNewItems,
    dealAssetUseBreakdownNewFieldItems,
  ]);

  const assetTypeChange: Function = useCallback((assetTypeItemId: number, assetUseBreakdownItemId: number): void => {
    if (onAssetTypeChange) {
      onAssetTypeChange(assetUseBreakdownItemId, assetTypeItemId);
    }
  }, []);

  useEffect((): void => {
    if (dealAssetUseBreakdownValues) {
      const initialDealAssetUseBreakdownTotalItems: Record<string, number> = {};

      Object.keys(dealAssetUseBreakdownValues).forEach((dealAssetUseBreakdownItemKey: string): void => {
        const dealAssetUseBreakdownItem: JsonObject = (
          dealAssetUseBreakdownValues[dealAssetUseBreakdownItemKey]
        );

        if (dealAssetUseBreakdownItem && dealAssetUseBreakdownItem.breakdownProperties) {
          Object.keys(dealAssetUseBreakdownItem.breakdownProperties).forEach((
            dealAssetUseBreakdownPropertyKey: string,
          ): void => {
            const breakdownPropertyId: string = FormService.extractIndexedFormFieldKey(
              dealAssetUseBreakdownPropertyKey,
            ) as string;
            const dealAssetUseBreakdownProperty: JsonObject = (
              dealAssetUseBreakdownItem.breakdownProperties[dealAssetUseBreakdownPropertyKey]
            );
            const currentTotalValue: number = (initialDealAssetUseBreakdownTotalItems
              && initialDealAssetUseBreakdownTotalItems[breakdownPropertyId]) || 0;

            if (dealAssetUseBreakdownProperty && dealAssetUseBreakdownProperty.value) {
              initialDealAssetUseBreakdownTotalItems[breakdownPropertyId] = currentTotalValue
                + FormatService.formatCleanNumber(dealAssetUseBreakdownProperty.value);
            }
          });
        }
      });

      setDealAssetUseBreakdownTotalItems(initialDealAssetUseBreakdownTotalItems);
    }
  }, [dealAssetUseBreakdownValues]);

  useEffect((): void => {
    setDealBuildingDetailsItems(dealBuildingDetailsData);
  }, [isEditMode, dealBuildingDetailsData]);

  return (
    <ExpansionGroup isExpanded isFixed headerComponent="Asset Use Breakdown">
      {
        (isEditMode || isNewDeal) ? (
          <CheckAccess permissions={dealBuildingDetailsItemEditPermissions}>
            <Table>
              <TableBody isPlain withZebraRows withBorderedCells withFixedCellsSizeCompressed>
                <TableRow>
                  <TableCell isDisabled>
                    Asset Type
                  </TableCell>
                  {
                    !withHeaders && (
                      <TableCell>
                        <CheckAccess permissions={dealBuildingDetailsItemCreatePermissions}>
                          <ActionButtonNewItem
                            withNoLabel
                            withNoPadding
                            withNoHighlight
                            onClick={addNewFieldDealAssetUseBreakdown}
                          />
                        </CheckAccess>
                      </TableCell>
                    )
                  }
                  {
                    dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdownHeaders
                    && dealBuildingDetailsItems.assetUseBreakdownHeaders.map((
                      dealAssetUseBreakdownHeader: DealAssetUseBreakdownHeader,
                      dealAssetUseBreakdownHeaderIndex: number,
                    ): ReactElement => {
                      const isLastItem: boolean = (
                        dealAssetUseBreakdownHeaderIndex
                        === dealBuildingDetailsItems.assetUseBreakdownHeaders.length - 1
                      );
                      const formKeyDealAssetUseBreakdownHeader: string = FormService.getIndexedFormFieldKey(
                        dealAssetUseBreakdownHeader.id,
                      );

                      return (
                        <TableCell key={dealAssetUseBreakdownHeader.id}>
                          <FlexContainer>
                            <FormFieldSelect
                              withNoWrap
                              withBlankElement
                              isDisabled={measureUnitList && !measureUnitList.length}
                              name={`${formPrefixDealAssetUseBreakdownHeaders}`
                                + `[${formKeyDealAssetUseBreakdownHeader}].name`}
                              optionsTotal={measureUnitList && measureUnitList.length}
                            >
                              {menuItemMeasureUnitList}
                            </FormFieldSelect>
                            <FlexContainer>
                              <CheckAccess permissions={dealBuildingDetailsItemDeletePermissions}>
                                <ActionButton
                                  isInline
                                  withNoPadding
                                  withNoHighlight
                                  withNoRipple
                                  onClick={(): void => {
                                    deleteField(dealAssetUseBreakdownHeader.id);
                                  }}
                                >
                                  <RemoveIcon />
                                </ActionButton>
                              </CheckAccess>
                              {
                                dealBuildingDetailsItems && !isAddNewFieldLimit
                                && !dealAssetUseBreakdownNewFieldItems.length && isLastItem && (
                                  <CheckAccess permissions={dealBuildingDetailsItemCreatePermissions}>
                                    <ActionButtonNewItem
                                      withNoLabel
                                      withNoPadding
                                      withNoHighlight
                                      onClick={addNewFieldDealAssetUseBreakdown}
                                    />
                                  </CheckAccess>
                                )
                              }
                            </FlexContainer>
                          </FlexContainer>
                        </TableCell>
                      );
                    })
                  }
                  {
                    dealAssetUseBreakdownNewFieldItems && !!dealAssetUseBreakdownNewFieldItems.length
                    && dealAssetUseBreakdownNewFieldItems.map((
                      dealAssetUseBreakdownItem: DealAssetUseBreakdownItem, dealAssetUseBreakdownIndex: number,
                    ): ReactElement => {
                      const isLastItem: boolean = dealAssetUseBreakdownIndex
                        === dealAssetUseBreakdownNewFieldItems.length - 1;
                      const formKeyDealAssetUseBreakdownItem: string = FormService.getIndexedFormFieldKey(
                        dealAssetUseBreakdownItem.id,
                      );

                      return (
                        <TableCell isActive={!isNewDeal} key={dealAssetUseBreakdownItem.id}>
                          <FlexContainer>
                            <FormFieldSelect
                              withNoWrap
                              withBlankElement
                              isDisabled={measureUnitList && !measureUnitList.length}
                              name={`${formPrefixDealAssetUseBreakdownHeaders}`
                                + `[${formKeyDealAssetUseBreakdownItem}].name`}
                              optionsTotal={measureUnitList && measureUnitList.length}
                            >
                              {menuItemMeasureUnitList}
                            </FormFieldSelect>
                            <FlexContainer>
                              <ActionButton
                                isInline
                                withNoPadding
                                withNoHighlight
                                withNoRipple
                                onClick={(): void => {
                                  deleteField(dealAssetUseBreakdownItem.id);
                                }}
                              >
                                <RemoveIcon />
                              </ActionButton>
                              {
                                dealBuildingDetailsItems && !isAddNewFieldLimit && isLastItem && (
                                  <CheckAccess permissions={dealBuildingDetailsItemCreatePermissions}>
                                    <ActionButtonNewItem
                                      withNoLabel
                                      withNoPadding
                                      withNoHighlight
                                      withNoRipple
                                      onClick={addNewFieldDealAssetUseBreakdown}
                                    />
                                  </CheckAccess>
                                )
                              }
                            </FlexContainer>
                          </FlexContainer>
                        </TableCell>
                      );
                    })
                  }
                </TableRow>
                {
                  withHeaders && dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdown
                  && !!dealBuildingDetailsItems.assetUseBreakdown.length
                  && dealBuildingDetailsItems.assetUseBreakdown.map((
                    dealAssetUseBreakdownItem: DealAssetUseBreakdownItem,
                  ): ReactElement => {
                    const formKeyDealAssetUseBreakdownItem: string = FormService.getIndexedFormFieldKey(
                      dealAssetUseBreakdownItem.id,
                    );

                    return (
                      <TableRow key={dealAssetUseBreakdownItem.id}>
                        <TableCell>
                          <FormFieldText
                            isHidden
                            name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].id`}
                          />
                          <FormFieldSelect
                            withBlankElement
                            isDisabled={assetTypeList && !assetTypeList.length}
                            name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].assetTypeId`}
                            optionsTotal={assetTypeList && assetTypeList.length}
                            onChange={((assetTypeItemId: number): void => {
                              assetTypeChange(assetTypeItemId, dealAssetUseBreakdownItem.id);
                            })}
                          >
                            {menuItemAssetTypeList}
                          </FormFieldSelect>
                        </TableCell>
                        {
                          [
                            ...dealAssetUseBreakdownItem.breakdownProperties,
                            ...dealAssetUseBreakdownNewFieldItems,
                          ].map((
                            dealAssetUseBreakdownPropertyItem: DealAssetUseBreakdownProperty,
                            dealAssetUseBreakdownPropertyIndex: number,
                            dealAssetUseBreakdownPropertyList: DealAssetUseBreakdownProperty[],
                          ): ReactElement => {
                            const isLastItem: boolean = (
                              dealAssetUseBreakdownPropertyIndex === dealAssetUseBreakdownPropertyList.length - 1
                            );
                            const isNewItem: boolean = !!dealAssetUseBreakdownNewFieldItems.find((
                              dealAssetUseBreakdownPropertyItemInternal: DealAssetUseBreakdownProperty,
                            ): boolean => (
                              dealAssetUseBreakdownPropertyItemInternal.id === dealAssetUseBreakdownPropertyItem.id
                            ));
                            const formKeyDealAssetUseBreakdownPropertyItem: string = FormService.getIndexedFormFieldKey(
                              dealAssetUseBreakdownPropertyItem.id,
                            );

                            return (
                              <TableCell isActive={isNewItem && !isNewDeal} key={dealAssetUseBreakdownPropertyItem.id}>
                                <FlexContainer>
                                  <FormFieldNumber
                                    isFloat
                                    name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].`
                                      + `breakdownProperties[${formKeyDealAssetUseBreakdownPropertyItem}].value`}
                                    placeholder="Value"
                                  />
                                  {
                                    isLastItem && (
                                      <FlexContainer>
                                        <Tooltip title="Delete">
                                          <ActionButton
                                            isInline
                                            withNoPadding
                                            onClick={(): void => {
                                              dealAssetUseBreakdownDeleteClick(dealAssetUseBreakdownItem.id);
                                            }}
                                          >
                                            <RemoveIcon />
                                          </ActionButton>
                                        </Tooltip>
                                      </FlexContainer>
                                    )
                                  }
                                </FlexContainer>
                              </TableCell>
                            );
                          })
                        }
                      </TableRow>
                    );
                  })
                }
                {
                  withHeaders && dealAssetUseBreakdownNewItems && !!dealAssetUseBreakdownNewItems.length
                  && dealAssetUseBreakdownNewItems.map((
                    dealAssetUseBreakdownItem: DealAssetUseBreakdownItem,
                  ): ReactElement => {
                    const formKeyDealAssetUseBreakdownItem: string = FormService.getIndexedFormFieldKey(
                      dealAssetUseBreakdownItem.id,
                    );

                    return (
                      <TableRow isActive={!isNewDeal} key={dealAssetUseBreakdownItem.id}>
                        <TableCell>
                          <FormFieldSelect
                            withBlankElement
                            isDisabled={assetTypeList && !assetTypeList.length}
                            name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].assetTypeId`}
                            optionsTotal={assetTypeList && assetTypeList.length}
                            onChange={((assetTypeItemId: number): void => {
                              assetTypeChange(assetTypeItemId, dealAssetUseBreakdownItem.id);
                            })}
                          >
                            {menuItemAssetTypeList}
                          </FormFieldSelect>
                        </TableCell>
                        {
                          [
                            ...dealAssetUseBreakdownItem.breakdownProperties,
                            ...dealAssetUseBreakdownNewFieldItems,
                          ].map((
                            dealAssetUseBreakdownPropertyItem: DealAssetUseBreakdownProperty,
                            dealAssetUseBreakdownPropertyIndex: number,
                            dealAssetUseBreakdownPropertyList: DealAssetUseBreakdownProperty[],
                          ): ReactElement => {
                            const isLastItem: boolean = (isEditMode || isNewDeal) && (
                              dealAssetUseBreakdownPropertyIndex === dealAssetUseBreakdownPropertyList.length - 1
                            );
                            const isNewItem: boolean = !!dealAssetUseBreakdownNewFieldItems.find((
                              dealAssetUseBreakdownPropertyItemInternal: DealAssetUseBreakdownProperty,
                            ): boolean => (
                              dealAssetUseBreakdownPropertyItemInternal.id === dealAssetUseBreakdownPropertyItem.id
                            ));
                            const formKeyDealAssetUseBreakdownPropertyItem: string = FormService.getIndexedFormFieldKey(
                              dealAssetUseBreakdownPropertyItem.id,
                            );

                            return (
                              <TableCell isActive={isNewItem && !isNewDeal} key={dealAssetUseBreakdownPropertyItem.id}>
                                <FlexContainer>
                                  <FormFieldText
                                    isHidden
                                    name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].`
                                      + `breakdownProperties[${formKeyDealAssetUseBreakdownPropertyItem}].id`}
                                  />
                                  <FormFieldNumber
                                    name={`${formPrefixBreakdown}[${formKeyDealAssetUseBreakdownItem}].`
                                      + `breakdownProperties[${formKeyDealAssetUseBreakdownPropertyItem}].value`}
                                    placeholder="Value"
                                  />
                                  {
                                    isLastItem && (
                                      <FlexContainer>
                                        <Tooltip title="Delete">
                                          <ActionButton
                                            isInline
                                            withNoPadding
                                            onClick={(): void => {
                                              dealAssetUseBreakdownDeleteClick(dealAssetUseBreakdownItem.id);
                                            }}
                                          >
                                            <RemoveIcon />
                                          </ActionButton>
                                        </Tooltip>
                                      </FlexContainer>
                                    )
                                  }
                                </FlexContainer>
                              </TableCell>
                            );
                          })
                        }
                      </TableRow>
                    );
                  })
                }
                {
                  (isEditMode || isNewDeal) && withHeaders && (
                    <CheckAccess permissions={dealBuildingDetailsItemCreatePermissions}>
                      <TableRowAddNewItem colSpan={columnsTotal} onClick={addNewItem} />
                    </CheckAccess>
                  )
                }
                {
                  dealAssetUseBreakdownTotalItems && withHeaders && (
                    <TableRowSummary withOffset withDarkBorders>
                      <TableCell
                        colSpan={Object.keys(dealAssetUseBreakdownTotalItems).length ? undefined : columnsTotal}
                      >
                        Total
                      </TableCell>
                      {
                        Object.entries(dealAssetUseBreakdownTotalItems)
                          .map(([totalItemId, totalItemValue]: [string, number]): ReactElement => (
                            <TableCell key={totalItemId} isTextRight={!totalItemValue}>
                              {(totalItemValue && FormatService.formatNumber(totalItemValue.toString(), true)) || '-'}
                            </TableCell>
                          ))
                      }
                    </TableRowSummary>
                  )
                }
              </TableBody>
            </Table>
          </CheckAccess>
        ) : (
          dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdown
          && !!dealBuildingDetailsItems.assetUseBreakdown.length && (
            <Table>
              <TableHead isSubHeader withFixedCellsSizeCompressed>
                <TableRow>
                  <TableCell>
                    Asset Type
                  </TableCell>
                  {
                    dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdownHeaders
                    && dealBuildingDetailsItems.assetUseBreakdownHeaders.map((
                      dealAssetUseBreakdownHeader: DealAssetUseBreakdownHeader,
                    ): ReactElement => (
                      <TableCell isTextRight key={dealAssetUseBreakdownHeader.id}>
                        {(measureUnitMap && dealAssetUseBreakdownHeader.name
                          && measureUnitMap[dealAssetUseBreakdownHeader.name]
                          && measureUnitMap[dealAssetUseBreakdownHeader.name].title)}
                      </TableCell>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody isPlain withZebraRows withBorderedCells withHeaderColumn withFixedCellsSizeCompressed>
                {
                  dealBuildingDetailsItems && dealBuildingDetailsItems.assetUseBreakdown
                  && dealBuildingDetailsItems.assetUseBreakdown
                    .map((dealAssetUseBreakdownItem: DealAssetUseBreakdownItem): ReactElement => (
                      <TableRow key={dealAssetUseBreakdownItem.id}>
                        <TableCell>
                          {dealAssetUseBreakdownItem.assetType && dealAssetUseBreakdownItem.assetType.title}
                        </TableCell>
                        {
                          dealAssetUseBreakdownItem.breakdownProperties
                            .map((dealAssetUseBreakdownProperty: DealAssetUseBreakdownProperty): ReactElement => (
                              <TableCell
                                isNumber
                                key={dealAssetUseBreakdownProperty.id}
                                isTextRight={!dealAssetUseBreakdownProperty.value}
                              >
                                {dealAssetUseBreakdownProperty.value || '-'}
                              </TableCell>
                            ))
                        }
                      </TableRow>
                    ))
                }
                {
                  !isEditMode && !isNewDeal && dealAssetUseBreakdownTotalItems && (
                    <TableRowSummary withOffset>
                      <TableCell>
                        Total
                      </TableCell>
                      {
                        Object.entries(dealAssetUseBreakdownTotalItems)
                          .map(([totalItemId, totalItemValue]: [string, number]): ReactElement => (
                            <TableCell isNumber isTextRight={!totalItemValue} key={totalItemId}>
                              {(totalItemValue && FormatService.formatNumber(totalItemValue.toString(), true)) || '-'}
                            </TableCell>
                          ))
                      }
                    </TableRowSummary>
                  )
                }
              </TableBody>
            </Table>
          )
        )
      }
    </ExpansionGroup>
  );
});

export default DealAssetUseBreakdown;
